/* eslint-disable no-param-reassign */
import { util } from 'echarts';
import { ThemeParser } from '../../../core/theme';
import { normalizeToArray } from '../../../util/utils';
import type { PresetMode } from '../..';

function parsePieSeries(
  pieSeriesOptions: Parameters<ThemeParser>[0]['series'],
  chart: Parameters<ThemeParser>[1],
  themeMode: PresetMode
) {
  const innerRadius = 48;
  const outerRadius = 80;
  const defaultOption = {
    radius: [innerRadius, outerRadius],
    avoidLabelOverlap: false,
    labelLine: {
      /**
       * @设计规范 饼图的圆环到标签的**最小距离**（即圆环到标签的水平距离），应该是32
       * @实现思路 最小距离包括引导线labelLine的两段直线长度length和length2、引导线与标签的间距distanceToLabelLine组成
       * labelLine两段长度最短时，圆环距标签最短，所以 32 = 16(length) + 6(length2) + 10(distanceToLabelLine)
       * @see length: https://echarts.apache.org/zh/option.html#series-pie.labelLine.length
       * @see length2: https://echarts.apache.org/zh/option.html#series-pie.labelLine.length2
       * @see distanceToLabelLine: https://echarts.apache.org/zh/option.html#series-pie.label.distanceToLabelLine
       */
      length: 16,
      length2: 6,
      lineStyle: {
        color: themeMode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'
      }
    },
    label: {
      show: true,
      lineHeight: 16,
      /**
       * @设计规范 饼图标签需要对齐视觉引导线labelLine。
       * @example 位于左侧的标签需要右对齐；位于右侧的标签需要左对齐。
       * @caution !!!!!alignTo: 'labelLine'要求饼图数据中不包含undefined类型的数据
       * @see https://echarts.apache.org/zh/option.html#series-pie.label.alignTo
       */
      alignTo: 'labelLine',
      bleedMargin: 16,
      distanceToLabelLine: 10,
      color: themeMode === 'light' ? '#000' : '#fff'
    },
    labelLayout: params => {
      // 左右两侧的标签到图表两侧的固定间距，文本宽度超出间距则缩略
      const bleedMargin = 16;
      // 标签到引导线labelLine的水平间距
      const distanceToLabelLine = 10;
      // 标签到饼图外接正方形的水平间距
      const distanceToSector = 32;
      const half = 0.5;
      // 饼图圆心的x坐标
      const ecIns = chart.getECharts();
      const viewWidth = ecIns.getWidth();
      const cx = viewWidth * half;
      const isLeftLabel = params.labelRect.x < cx;
      const labelLineX = cx + (outerRadius + distanceToSector) * (isLeftLabel ? -1 : 1);
      const labelX = labelLineX + distanceToLabelLine * (isLeftLabel ? -1 : 1);
      return {
        x: labelX,
        width: isLeftLabel ? labelX - bleedMargin : viewWidth - bleedMargin - labelX,
        hideOverlap: true,
        labelLinePoints: [
          params.labelLinePoints[0],
          params.labelLinePoints[1],
          [labelLineX, params.labelLinePoints[2][1]]
        ]
      };
    },
    emphasis: {
      focus: 'self',
      scaleSize: 10,
      itemStyle: {
        borderWidth: 1,
        borderColor: themeMode === 'light' ? '#fff' : '#171717',
        color: 'inherit'
      }
    },
    /**
     * @设计规范 饼图中各扇形切换到高亮状态时，需要弱化其余扇形数据并隐藏引导线、标签。
     * @remarks
     * 主题配置实现思路：设置高亮状态下聚焦当前图形`foucs: ''self`，并设置每个扇形弱化状态下的样式`series.blur`如下：
     *  1、弱化状态下标签颜色设置为透明。
     *  2、引导线`labelLine`的线段样式设置不透明度为0
     *  3、图形本身保持不透明度为1，覆盖echarts默认聚焦设置。
     * @see https://github.com/apache/echarts/blob/master/src/util/states.ts#L345 聚焦条件下图形弱化为opacity：0.1
     */
    blur: {
      label: {
        opacity: 0
      },
      labelLine: {
        show: true,
        lineStyle: {
          opacity: 0
        }
      },
      itemStyle: {
        opacity: 1
      }
    }
  };

  (pieSeriesOptions as []).forEach((series, index) => {
    // @ts-ignore
    if (series.type !== 'pie') {
      return;
    }
    const defaultPieOption = util.clone(defaultOption);
    pieSeriesOptions[index] = util.merge(defaultPieOption, series, true);
  });
}

export function pieThemeParse(...args: Parameters<ThemeParser>) {
  const [option, chart] = args;
  option.series = normalizeToArray(option.series);
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';
  parsePieSeries(option.series, chart, themeMode);
}
